import * as React from 'react';
import { IPortalMe, portalCall, startViewAs } from './auth';
import { EmptyArt, Icon } from './brand';
import { AdminLayout, ProviderTag } from './Admin';

// /admin/team - People & roles for one organization.
//   People: invite by email, change roles, remove/restore access, the default role for the organization's own accounts.
//   Roles:  the organization's roles and exactly what each can do (Owner is fixed).
// The API enforces all of it; this screen only offers what the signed-in person is allowed to do.

interface IPermission { key: string; group: string; label: string; help: string; }
interface IRole { key: string; name: string; description: string; permissions: string[]; system: boolean; members: number; assignable: boolean; }
/** The latest invitation email: accepted by Exchange (sent), refused (failed), or reported back as delayed/bounced. */
interface IDelivery { status: 'sent' | 'failed' | 'delayed' | 'bounced'; sentAt: string; detail: string; }
interface IMember { email: string; name: string; roleKey: string | null; status: 'invited' | 'active' | 'removed'; linkPending: boolean; delivery: IDelivery | null; invitedBy: string; invitedAt: string; inviteExpires: string; acceptedAt: string; lastSeen: string; external: boolean; }
interface ITeam {
  client: { tenantId: string; name: string; provider: 'entra' | 'google'; domain: string; defaultRoleKey: string | null };
  roles: IRole[];
  permissions: IPermission[];
  members: IMember[];
  can: { invite: boolean; assign: boolean; roles: boolean };
  me: string;
}
interface IInviteResult { link: string; emailSent: boolean; emailError: string; }

// Mirrors api/src/access.ts: ticking a permission also ticks what it needs.
const IMPLIES: { [key: string]: string[] } = {
  'runs.edit': ['runs.view'], 'runs.complete': ['runs.view'], 'runs.delete': ['runs.view'],
  'batches.pdf': ['batches.view'], 'batches.reverse': ['batches.view'],
  'qc.record': ['batches.view'], 'qc.release': ['batches.view'],
  'stock.receive': ['stock.view'], 'stock.adjust': ['stock.view'], 'stock.items': ['stock.view'], 'finished.ship': ['stock.view'],
  'formulas.amounts': ['formulas.view'], 'formulas.edit': ['formulas.amounts', 'formulas.view'], 'formulas.guides': ['formulas.view'],
  'history.restore': ['history.view'], 'people.roles': ['people.assign'], 'people.assign': ['people.invite']
};
const withImplied = (keys: string[]): string[] => {
  const out: Set<string> = new Set();
  const add = (k: string): void => { if (!out.has(k)) { out.add(k); (IMPLIES[k] || []).forEach(add); } };
  keys.forEach(add);
  return Array.from(out);
};
const neededBy = (key: string, keys: string[]): string[] => keys.filter((k: string): boolean => k !== key && withImplied([k]).indexOf(key) !== -1);

const day = (iso: string): string => (iso ? new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: new Date(iso).getFullYear() === new Date().getFullYear() ? undefined : 'numeric' }) : '');
const ago = (iso: string): string => {
  if (!iso) { return ''; }
  const mins: number = Math.round((Date.now() - new Date(iso).getTime()) / 60000);
  if (mins < 2) { return 'just now'; }
  if (mins < 60) { return `${mins} min ago`; }
  if (mins < 60 * 24) { return `${Math.round(mins / 60)} h ago`; }
  if (mins < 60 * 24 * 14) { const d: number = Math.round(mins / 1440); return `${d} day${d === 1 ? '' : 's'} ago`; }
  return day(iso);
};
const initials = (name: string, email: string): string => {
  const parts: string[] = (name.trim() !== '' ? name : email).split(/[\s.@_-]+/).filter((x: string): boolean => x !== '');
  return ((parts[0] || '?').charAt(0) + (parts.length > 1 ? parts[1].charAt(0) : '')).toUpperCase();
};

export const TeamAdmin: React.FunctionComponent<{ me: IPortalMe }> = ({ me }) => {
  const initialTenant: string = new URLSearchParams(window.location.search).get('tenantId') || (me.client !== null ? me.client.tenantId : '');
  const [tenantId, setTenantId] = React.useState<string>(initialTenant);
  const [clients, setClients] = React.useState<{ tenantId: string; name: string }[]>([]);
  const [team, setTeam] = React.useState<ITeam | null>(null);
  const [tab, setTab] = React.useState<'people' | 'roles'>('people');
  const [search, setSearch] = React.useState<string>('');
  const [msg, setMsg] = React.useState<{ ok: boolean; text: string } | null>(null);
  const [busy, setBusy] = React.useState<string>('');
  const [invite, setInvite] = React.useState<{ email: string; roleKey: string; message: string; result: IInviteResult | null; error: string; resend?: IMember } | null>(null);
  const [roleEdit, setRoleEdit] = React.useState<{ key: string; name: string; description: string; permissions: string[]; system: boolean; error: string } | null>(null);
  const [copied, setCopied] = React.useState<boolean>(false);

  const load = async (tid: string = tenantId): Promise<void> => {
    try { setTeam(await portalCall<ITeam>('GET', `team${tid ? `?tenantId=${tid}` : ''}`)); }
    catch (e) { setTeam(null); setMsg({ ok: false, text: (e as Error).message }); }
  };
  React.useEffect((): void => {
    void load();
    if (me.isOwner) { portalCall<{ tenantId: string; name: string }[]>('GET', 'clients').then(setClients).catch((): void => undefined); }
  }, []);

  /** Runs a change, shows the outcome and reloads. */
  const act = async (label: string, fn: () => Promise<unknown>, ok: string): Promise<boolean> => {
    setBusy(label); setMsg(null);
    try { await fn(); setMsg({ ok: true, text: ok }); await load(); return true; }
    catch (e) { setMsg({ ok: false, text: (e as Error).message }); return false; }
    finally { setBusy(''); }
  };
  const body = (extra: object): object => ({ tenantId: team !== null ? team.client.tenantId : tenantId, ...extra });

  if (me.client === null && !me.isOwner) {
    return <AdminLayout me={me} section="team"><div className="bu-alert bu-alert-info">Your organization isn't a client yet.</div></AdminLayout>;
  }

  const roleName = (key: string | null): string => {
    if (team === null) { return ''; }
    const k: string | null = key || team.client.defaultRoleKey;
    return (team.roles.filter((r: IRole): boolean => r.key === k)[0] || { name: k ? k : 'No access' }).name;
  };
  const assignable: IRole[] = team !== null ? team.roles.filter((r: IRole): boolean => r.assignable) : [];
  const members: IMember[] = (team !== null ? team.members : []).filter((m: IMember): boolean =>
    search.trim() === '' || `${m.name} ${m.email}`.toLowerCase().indexOf(search.trim().toLowerCase()) !== -1);
  const counts = { active: 0, invited: 0, removed: 0 };
  (team !== null ? team.members : []).forEach((m: IMember): void => { counts[m.status]++; });
  const orgAccounts: string = team === null ? '' : team.client.provider === 'google' ? `@${team.client.domain} Google accounts` : `${team.client.name}'s Microsoft 365 accounts`;

  const copyLink = (link: string): void => {
    void navigator.clipboard.writeText(link).then((): void => { setCopied(true); window.setTimeout((): void => setCopied(false), 1600); });
  };

  // ----- People -----

  const deliveryLine = (m: IMember): React.ReactElement | null => {
    const d: IDelivery | null = m.delivery;
    if (d === null || !(m.status === 'invited' || m.linkPending)) { return null; }
    const when: string = new Date(d.sentAt).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
    const look: { color: string; text: string } =
      d.status === 'bounced' ? { color: 'var(--danger)', text: `Email bounced: ${d.detail}` }
        : d.status === 'failed' ? { color: 'var(--danger)', text: 'Email not sent' }
          : d.status === 'delayed' ? { color: 'var(--warn)', text: 'Email delivery delayed' }
            : { color: 'var(--text-3)', text: `Email sent ${when}` };
    return (
      <div className="bu-person-mail" style={{ color: look.color, display: 'flex', gap: 5, alignItems: 'center', marginTop: 3, fontWeight: d.status === 'sent' ? 500 : 600 }}
        title={d.status === 'sent' ? 'Accepted by the mail system. A bounce shows here if it comes back.' : `${d.detail} (sent ${when})`}>
        <Icon name="mail" size={13} />{look.text}
      </div>
    );
  };

  const statusCell = (m: IMember): React.ReactElement => {
    if (m.status === 'invited') {
      const expired: boolean = m.inviteExpires !== '' && new Date(m.inviteExpires).getTime() < Date.now();
      return (
        <div>
          <span className={`bu-badge ${expired ? 'bu-badge-danger' : 'bu-badge-warn'}`}>{expired ? 'Invite expired' : 'Invited'}</span>
          <div className="bu-person-mail" style={{ marginTop: 4 }}>{expired ? `Sent ${day(m.invitedAt)}` : `Expires ${day(m.inviteExpires)}`}{m.invitedBy ? ` · by ${m.invitedBy}` : ''}</div>
          {deliveryLine(m)}
        </div>
      );
    }
    if (m.status === 'removed') { return <span className="bu-badge bu-badge-off">Access removed</span>; }
    return (
      <div>
        <span className="bu-badge bu-badge-ok">Active</span>
        <div className="bu-person-mail" style={{ marginTop: 4 }}>{m.lastSeen ? `Last seen ${ago(m.lastSeen)}` : 'Not signed in yet'}</div>
        {m.linkPending && m.inviteExpires !== '' && (
          <div className="bu-person-mail" title="They keep their access; the new link re-links the account they sign in with">
            {new Date(m.inviteExpires).getTime() < Date.now() ? 'Invite link expired' : `Invite resent ${day(m.invitedAt)}`}
          </div>
        )}
        {deliveryLine(m)}
      </div>
    );
  };

  const roleCell = (t: ITeam, m: IMember): React.ReactElement => {
    const current: IRole | undefined = t.roles.filter((r: IRole): boolean => r.key === (m.roleKey || t.client.defaultRoleKey))[0];
    const editable: boolean = t.can.assign && m.status !== 'removed' && (current === undefined || current.assignable);
    if (!editable) {
      return <span style={{ fontWeight: 600 }}>{roleName(m.roleKey)}{m.roleKey === null && t.client.defaultRoleKey !== null && <span style={{ color: 'var(--text-3)', fontWeight: 500 }}> (default)</span>}</span>;
    }
    return (
      <select className="bu-select" style={{ width: '100%' }} value={m.roleKey || ''} disabled={busy !== ''} aria-label={`Role for ${m.email}`}
        onChange={(e: React.ChangeEvent<HTMLSelectElement>): void => {
          const next: string = e.target.value;
          void act('role', () => portalCall('PUT', 'team/members', body({ email: m.email, roleKey: next === '' ? null : next })), `${m.name || m.email} is now ${roleName(next === '' ? null : next)}.`);
        }}>
        {t.client.defaultRoleKey !== null && <option value="">{roleName(null)} (default)</option>}
        {t.roles.filter((r: IRole): boolean => r.assignable || r.key === m.roleKey).map((r: IRole) => <option key={r.key} value={r.key}>{r.name}</option>)}
      </select>
    );
  };

  const actions = (t: ITeam, m: IMember): React.ReactElement | null => {
    const canResend: boolean = t.can.invite && m.email !== t.me && (m.status !== 'removed' || t.can.assign);
    // platform owner: see the apps exactly as this person does (read-only)
    const canViewAs: boolean = me.isOwner && !me.viewedBy && m.status === 'active' && m.email !== t.me;
    if (!t.can.assign && !canResend && !canViewAs) { return null; }
    const who: string = m.name || m.email;
    const expired: boolean = (m.status === 'invited' && m.inviteExpires !== '' && new Date(m.inviteExpires).getTime() < Date.now())
      || (m.delivery !== null && (m.delivery.status === 'bounced' || m.delivery.status === 'failed') && (m.status === 'invited' || m.linkPending));
    const resendLabel: string = m.status === 'removed' ? 'Re-invite' : m.status === 'active' && !m.lastSeen ? 'Send invite' : 'Resend';
    return (
      <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
        {canViewAs && (
          <button className="bu-btn bu-btn-ghost bu-btn-sm" disabled={busy !== ''} title={`See BeefedUp Apps as ${who} sees it (read-only)`}
            onClick={(): void => startViewAs({ tenantId: t.client.tenantId, email: m.email, name: who, clientName: t.client.name })}>View as</button>
        )}
        {canResend && (
          <button className={`bu-btn ${expired ? 'bu-btn-secondary' : 'bu-btn-ghost'} bu-btn-sm`} disabled={busy !== ''}
            onClick={(): void => setInvite({ email: m.email, roleKey: m.roleKey || t.client.defaultRoleKey || '', message: '', result: null, error: '', resend: m })}>
            <Icon name="mail" size={15} /> {resendLabel}
          </button>
        )}
        {t.can.assign && m.status !== 'removed' && (
          <button className="bu-btn bu-btn-ghost bu-btn-sm" style={{ color: 'var(--danger)' }} disabled={busy !== ''} onClick={(): void => {
            const q: string = m.status === 'invited' ? `Withdraw the invitation to ${m.email}?` : `Remove ${who}'s access to ${t.client.name}? They'll be signed out of it within a minute.`;
            if (window.confirm(q)) { void act('remove', () => portalCall('POST', 'team/members/remove', body({ email: m.email })), m.status === 'invited' ? 'Invitation withdrawn.' : `${who} no longer has access.`); }
          }}>{m.status === 'invited' ? 'Withdraw' : 'Remove'}</button>
        )}
        {t.can.assign && m.status === 'removed' && (
          <button className="bu-btn bu-btn-secondary bu-btn-sm" disabled={busy !== ''} onClick={(): void => {
            void act('restore', () => portalCall('POST', 'team/members/restore', body({ email: m.email })), `${who}'s access is back.`);
          }}>Restore</button>
        )}
      </div>
    );
  };

  const renderPeople = (t: ITeam): React.ReactElement => (
    <React.Fragment>
      <div className="bu-card bu-card-pad" style={{ display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap', marginBottom: 18 }}>
        <div className="bu-feature-icon" style={{ margin: 0 }}><Icon name="key" /></div>
        <div style={{ flex: 1, minWidth: 240 }}>
          <strong style={{ fontSize: 14.5 }}>Anyone signing in with {orgAccounts}</strong>
          <div style={{ color: 'var(--text-2)', fontSize: 13.5, marginTop: 2 }}>
            {t.client.defaultRoleKey === null ? 'gets no access until they are invited.' : `gets the ${roleName(null)} role until you choose another for them. People from outside need an invitation.`}
          </div>
        </div>
        {t.can.roles ? (
          <select className="bu-select" style={{ flex: '0 0 auto', width: 240 }} value={t.client.defaultRoleKey || ''} disabled={busy !== ''} aria-label="Default role"
            onChange={(e: React.ChangeEvent<HTMLSelectElement>): void => {
              const v: string = e.target.value;
              void act('default', () => portalCall('PUT', 'team/settings', body({ roleKey: v === '' ? null : v })), v === '' ? 'Only invited people can get in now.' : `New people from ${t.client.name} get ${roleName(v)}.`);
            }}>
            <option value="">No access (invite only)</option>
            {t.roles.filter((r: IRole): boolean => !r.system).map((r: IRole) => <option key={r.key} value={r.key}>{r.name}</option>)}
          </select>
        ) : <span className="bu-chip">{t.client.defaultRoleKey === null ? 'Invite only' : roleName(null)}</span>}
      </div>

      <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 14, flexWrap: 'wrap' }}>
        <input className="bu-input" style={{ maxWidth: 280 }} placeholder="Search people" value={search} onChange={(e: React.ChangeEvent<HTMLInputElement>): void => setSearch(e.target.value)} />
        <span style={{ color: 'var(--text-3)', fontSize: 13, fontWeight: 600 }}>{counts.active} active · {counts.invited} invited{counts.removed > 0 ? ` · ${counts.removed} removed` : ''}</span>
        <span className="bu-spacer" style={{ flex: 1 }} />
        {t.can.invite && (
          <button className="bu-btn bu-btn-primary" onClick={(): void => {
            const def: IRole | undefined = assignable.filter((r: IRole): boolean => r.key === t.client.defaultRoleKey)[0] || assignable.filter((r: IRole): boolean => !r.system)[0];
            setInvite({ email: '', roleKey: def ? def.key : '', message: '', result: null, error: '' });
          }}><Icon name="plus" size={16} /> Invite people</button>
        )}
      </div>

      <div className="bu-card" style={{ overflow: 'hidden' }}>
        {t.members.length === 0 ? (
          <div className="bu-empty"><EmptyArt /><h3>No one here yet</h3><p>Invite your team, or ask them to sign in with {orgAccounts}.</p></div>
        ) : (
          <div className="bu-table-wrap">
            <table className="bu-table bu-people">
              <thead><tr><th>Person</th><th className="bu-hide-narrow" style={{ width: 250 }}>Role</th><th style={{ width: 210 }}>Status</th><th style={{ width: 170 }} /></tr></thead>
              <tbody>
                {members.map((m: IMember) => (
                  <tr key={m.email} style={{ opacity: m.status === 'removed' ? 0.6 : 1 }}>
                    <td>
                      <div className="bu-person">
                        <span className="bu-avatar">{initials(m.name, m.email)}</span>
                        <div style={{ minWidth: 0 }}>
                          <div className="bu-person-name">{m.name || m.email.split('@')[0]}{m.email === t.me && <span className="bu-chip" style={{ marginLeft: 8, height: 20 }}>You</span>}</div>
                          <div className="bu-person-mail">{m.email}{m.external && <span title="Signed in with an account from another organization"> · outside organization</span>}</div>
                          <div className="bu-only-narrow" style={{ marginTop: 8 }}>{roleCell(t, m)}</div>
                        </div>
                      </div>
                    </td>
                    <td className="bu-hide-narrow">{roleCell(t, m)}</td>
                    <td>{statusCell(m)}</td>
                    <td style={{ textAlign: 'right' }}>{actions(t, m)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </React.Fragment>
  );

  // ----- Roles -----

  const renderRoles = (t: ITeam): React.ReactElement => (
    <React.Fragment>
      <p className="bu-lede" style={{ marginTop: 0, marginBottom: 18 }}>
        A role is a set of permissions. Change a role and everyone who has it changes with it.{!t.can.roles && ' Your role can view roles but not change them.'}
      </p>
      <div className="bu-roles">
        {t.roles.map((r: IRole) => (
          <button key={r.key} className="bu-role" onClick={(): void => setRoleEdit({ key: r.key, name: r.name, description: r.description, permissions: r.permissions, system: r.system, error: '' })}>
            <h3>{r.system && <Icon name="key" size={16} />}{r.name}{t.client.defaultRoleKey === r.key && <span className="bu-chip" style={{ height: 20, fontSize: 11 }}>Default</span>}</h3>
            <p>{r.description || 'No description.'}</p>
            <div className="bu-role-meta">{r.permissions.length} of {t.permissions.length} permissions · {r.members} {r.members === 1 ? 'person' : 'people'}</div>
          </button>
        ))}
        {t.can.roles && (
          <button className="bu-role" style={{ borderStyle: 'dashed', alignItems: 'center', justifyContent: 'center', color: 'var(--text-2)', minHeight: 120 }}
            onClick={(): void => setRoleEdit({ key: '', name: '', description: '', permissions: ['runs.view', 'batches.view', 'stock.view', 'formulas.view'], system: false, error: '' })}>
            <Icon name="plus" size={22} /><strong>New role</strong>
          </button>
        )}
      </div>
    </React.Fragment>
  );

  const myPerms: string[] = me.isOwner ? (team !== null ? team.permissions.map((p: IPermission): string => p.key) : []) : me.permissions;

  const renderRoleDrawer = (t: ITeam): React.ReactElement | null => {
    if (roleEdit === null) { return null; }
    const readOnly: boolean = roleEdit.system || !t.can.roles;
    const groups: string[] = Array.from(new Set(t.permissions.map((p: IPermission): string => p.group)));
    const toggle = (key: string, on: boolean): void => {
      let next: string[] = on ? withImplied([...roleEdit.permissions, key]) : roleEdit.permissions.filter((k: string): boolean => k !== key);
      if (!on) { next = next.filter((k: string): boolean => withImplied([k]).indexOf(key) === -1); }   // drop what needed it
      setRoleEdit({ ...roleEdit, permissions: next });
    };
    const save = async (): Promise<void> => {
      setBusy('role-save');
      try {
        await portalCall('PUT', 'team/roles', body({ key: roleEdit.key || undefined, name: roleEdit.name, description: roleEdit.description, permissions: roleEdit.permissions }));
        setMsg({ ok: true, text: `${roleEdit.name} saved. Changes apply within a minute.` });
        setRoleEdit(null);
        await load();
      } catch (e) { setRoleEdit({ ...roleEdit, error: (e as Error).message }); } finally { setBusy(''); }
    };
    const used: number = (t.roles.filter((r: IRole): boolean => r.key === roleEdit.key)[0] || { members: 0 }).members;
    return (
      <div className="bu-drawer-backdrop" onClick={(): void => setRoleEdit(null)}>
        <div className="bu-drawer" style={{ width: 'min(620px, 100%)' }} onClick={(e: React.MouseEvent<HTMLDivElement>): void => e.stopPropagation()}>
          <div className="bu-drawer-head">
            <div>
              <p className="bu-eyebrow" style={{ marginBottom: 4 }}>{roleEdit.key === '' ? 'New role' : readOnly ? 'Role' : 'Edit role'}</p>
              <h2 className="bu-card-title" style={{ fontSize: 20 }}>{roleEdit.name || 'Untitled role'}</h2>
            </div>
            <button className="bu-btn bu-btn-ghost bu-btn-sm" onClick={(): void => setRoleEdit(null)} aria-label="Close">&times;</button>
          </div>
          <div className="bu-drawer-body">
            {roleEdit.system && <div className="bu-alert bu-alert-info"><Icon name="key" size={18} /><span>Owners can do everything, including managing people and roles. Every organization keeps at least one Owner.</span></div>}
            <label className="bu-field">Name
              <input className="bu-input" value={roleEdit.name} disabled={readOnly} maxLength={100} onChange={(e: React.ChangeEvent<HTMLInputElement>): void => setRoleEdit({ ...roleEdit, name: e.target.value })} />
            </label>
            <label className="bu-field">Description <small>Shown when choosing a role.</small>
              <input className="bu-input" value={roleEdit.description} disabled={readOnly} maxLength={400} onChange={(e: React.ChangeEvent<HTMLInputElement>): void => setRoleEdit({ ...roleEdit, description: e.target.value })} />
            </label>
            <div className="bu-field">Permissions <small>{roleEdit.permissions.length} selected{!me.isOwner && !readOnly ? ". You can only give permissions your own role has." : ''}</small></div>
            <div>
              {groups.map((g: string) => (
                <div key={g} className="bu-perm-group">
                  <h4>{g}</h4>
                  {t.permissions.filter((p: IPermission): boolean => p.group === g).map((p: IPermission) => {
                    const on: boolean = roleEdit.permissions.indexOf(p.key) !== -1;
                    const allowed: boolean = !readOnly && (myPerms.indexOf(p.key) !== -1);
                    const needers: string[] = on ? neededBy(p.key, roleEdit.permissions) : [];
                    return (
                      <label key={p.key} className={`bu-perm${allowed ? '' : ' off'}`}>
                        <input type="checkbox" checked={on} disabled={!allowed} onChange={(e: React.ChangeEvent<HTMLInputElement>): void => toggle(p.key, e.target.checked)} />
                        <div>
                          <strong>{p.label}{p.key === 'formulas.amounts' && <span className="bu-chip" style={{ marginLeft: 8, height: 20, fontSize: 11, display: 'inline-flex' }}>Proprietary</span>}</strong>
                          <span>{p.help}{needers.length > 0 && allowed ? ` · needed by ${needers.map((k: string): string => (t.permissions.filter((x: IPermission): boolean => x.key === k)[0] || { label: k }).label).join(', ')}` : ''}</span>
                        </div>
                      </label>
                    );
                  })}
                </div>
              ))}
            </div>
            {roleEdit.error !== '' && <div className="bu-alert bu-alert-danger">{roleEdit.error}</div>}
          </div>
          <div className="bu-drawer-foot">
            {!readOnly && roleEdit.key !== '' && (
              <button className="bu-btn bu-btn-ghost" style={{ color: 'var(--danger)', marginRight: 'auto' }} disabled={busy !== '' || used > 0 || t.client.defaultRoleKey === roleEdit.key}
                title={used > 0 ? 'Give its people another role first' : t.client.defaultRoleKey === roleEdit.key ? 'Choose another default role first' : ''}
                onClick={(): void => {
                  if (!window.confirm(`Delete the ${roleEdit.name} role?`)) { return; }
                  const k: string = roleEdit.key;
                  void act('role-delete', () => portalCall('POST', 'team/roles/delete', body({ key: k })), 'Role deleted.').then((ok: boolean): void => { if (ok) { setRoleEdit(null); } });
                }}>Delete role</button>
            )}
            <button className="bu-btn bu-btn-ghost" onClick={(): void => setRoleEdit(null)}>{readOnly ? 'Close' : 'Cancel'}</button>
            {!readOnly && <button className="bu-btn bu-btn-primary" disabled={busy !== '' || roleEdit.name.trim() === ''} onClick={(): void => { void save(); }}>{busy === 'role-save' ? 'Saving...' : roleEdit.key === '' ? 'Create role' : 'Save role'}</button>}
          </div>
        </div>
      </div>
    );
  };

  // ----- Invite drawer -----

  const renderInvite = (t: ITeam): React.ReactElement | null => {
    if (invite === null) { return null; }
    const send = async (): Promise<void> => {
      setBusy('invite');
      try {
        const r: IInviteResult = invite.resend !== undefined
          ? await portalCall<IInviteResult>('POST', 'team/invites/resend', body({ email: invite.email, message: invite.message }))
          : await portalCall<IInviteResult>('POST', 'team/invites', body({ email: invite.email.trim(), roleKey: invite.roleKey, message: invite.message }));
        setInvite({ ...invite, result: r, error: '' });
        await load();
      } catch (e) { setInvite({ ...invite, error: (e as Error).message }); } finally { setBusy(''); }
    };
    const role: IRole | undefined = t.roles.filter((r: IRole): boolean => r.key === invite.roleKey)[0];
    return (
      <div className="bu-drawer-backdrop" onClick={(): void => setInvite(null)}>
        <div className="bu-drawer" onClick={(e: React.MouseEvent<HTMLDivElement>): void => e.stopPropagation()}>
          <div className="bu-drawer-head">
            <div>
              <p className="bu-eyebrow" style={{ marginBottom: 4 }}>{t.client.name}</p>
              <h2 className="bu-card-title" style={{ fontSize: 20 }}>{invite.result !== null ? 'Invitation ready' : invite.resend !== undefined ? (invite.resend.status === 'removed' ? 'Invite again' : 'Resend invitation') : 'Invite someone'}</h2>
            </div>
            <button className="bu-btn bu-btn-ghost bu-btn-sm" onClick={(): void => setInvite(null)} aria-label="Close">&times;</button>
          </div>
          <div className="bu-drawer-body">
            {invite.result === null && invite.resend !== undefined ? (
              <React.Fragment>
                <div className="bu-field">To
                  <div className="bu-person" style={{ marginTop: 4 }}>
                    <span className="bu-avatar">{initials(invite.resend.name, invite.resend.email)}</span>
                    <div style={{ minWidth: 0 }}>
                      <div className="bu-person-name" style={{ color: 'var(--text)' }}>{invite.resend.name || invite.resend.email.split('@')[0]}</div>
                      <div className="bu-person-mail">{invite.resend.email} · {roleName(invite.resend.roleKey)}</div>
                    </div>
                  </div>
                </div>
                <div className="bu-alert bu-alert-info">
                  <Icon name="mail" size={18} />
                  <span>{invite.resend.status === 'invited'
                    ? 'They get a new link that works for 14 days. The earlier link stops working.'
                    : invite.resend.status === 'removed'
                      ? 'Their access comes back, with the same role, once they accept.'
                      : 'They keep their access in the meantime. The link signs them in and re-links the account they use, which helps if they changed accounts.'}</span>
                </div>
                <label className="bu-field">Personal message <small>Optional. Included in the email.</small>
                  <textarea className="bu-input" rows={3} autoFocus={true} style={{ height: 'auto', paddingTop: 10, resize: 'vertical' }} maxLength={1000} value={invite.message}
                    onChange={(e: React.ChangeEvent<HTMLTextAreaElement>): void => setInvite({ ...invite, message: e.target.value })} />
                </label>
                {invite.error !== '' && <div className="bu-alert bu-alert-danger">{invite.error}</div>}
              </React.Fragment>
            ) : invite.result === null ? (
              <React.Fragment>
                <label className="bu-field">Work email <small>They sign in with this exact account (Microsoft 365 or Google Workspace).</small>
                  <input className="bu-input" type="email" autoFocus={true} placeholder="name@company.com" value={invite.email}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>): void => setInvite({ ...invite, email: e.target.value })} />
                </label>
                <label className="bu-field">Role
                  <select className="bu-select" style={{ height: 42 }} value={invite.roleKey} onChange={(e: React.ChangeEvent<HTMLSelectElement>): void => setInvite({ ...invite, roleKey: e.target.value })}>
                    {assignable.map((r: IRole) => <option key={r.key} value={r.key}>{r.name}</option>)}
                  </select>
                  {role !== undefined && <small>{role.description}</small>}
                </label>
                <label className="bu-field">Personal message <small>Optional. Included in the email.</small>
                  <textarea className="bu-input" rows={3} style={{ height: 'auto', paddingTop: 10, resize: 'vertical' }} maxLength={1000} value={invite.message}
                    onChange={(e: React.ChangeEvent<HTMLTextAreaElement>): void => setInvite({ ...invite, message: e.target.value })} />
                </label>
                {invite.error !== '' && <div className="bu-alert bu-alert-danger">{invite.error}</div>}
              </React.Fragment>
            ) : (
              <React.Fragment>
                {invite.result.emailSent
                  ? <div className="bu-alert bu-alert-ok"><Icon name="check" size={18} /><span>We emailed an invitation to <strong>{invite.email}</strong>. It works for 14 days.</span></div>
                  : <div className="bu-alert bu-alert-danger"><span>The invitation is ready, but the email couldn't be sent ({invite.result.emailError}). Send them the link below yourself.</span></div>}
                <div className="bu-field">Invitation link <small>Only works for {invite.email}, once.</small>
                  <div className="bu-linkbox">
                    <code>{invite.result.link}</code>
                    <button className="bu-btn bu-btn-secondary bu-btn-sm" onClick={(): void => copyLink(invite.result!.link)}><Icon name={copied ? 'check' : 'link'} size={15} /> {copied ? 'Copied' : 'Copy'}</button>
                  </div>
                </div>
              </React.Fragment>
            )}
          </div>
          <div className="bu-drawer-foot">
            {invite.result === null ? (
              <React.Fragment>
                <button className="bu-btn bu-btn-ghost" onClick={(): void => setInvite(null)}>Cancel</button>
                <button className="bu-btn bu-btn-primary" disabled={busy !== '' || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(invite.email.trim()) || (invite.resend === undefined && invite.roleKey === '')} onClick={(): void => { void send(); }}>
                  <Icon name="mail" size={16} /> {busy === 'invite' ? 'Sending...' : invite.resend !== undefined ? 'Resend invitation' : 'Send invitation'}</button>
              </React.Fragment>
            ) : (
              <React.Fragment>
                {invite.resend === undefined && <button className="bu-btn bu-btn-ghost" onClick={(): void => setInvite({ email: '', roleKey: invite.roleKey, message: invite.message, result: null, error: '' })}>Invite another</button>}
                <button className="bu-btn bu-btn-primary" onClick={(): void => setInvite(null)}>Done</button>
              </React.Fragment>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <AdminLayout me={me} section="team">
      <div className="bu-page-head">
        <div>
          <p className="bu-eyebrow">{team !== null ? team.client.name : me.client !== null ? me.client.name : 'Admin'}</p>
          <h1 className="bu-title">People &amp; roles</h1>
          <p className="bu-lede">Who can open {team !== null ? team.client.name : 'your organization'} in BeefedUp Apps, and what each person can see and do.</p>
        </div>
        {me.isOwner && clients.length > 0 && (
          <select className="bu-select" style={{ height: 40 }} value={team !== null ? team.client.tenantId : tenantId} aria-label="Organization"
            onChange={(e: React.ChangeEvent<HTMLSelectElement>): void => { setTenantId(e.target.value); setTeam(null); setMsg(null); void load(e.target.value); }}>
            {clients.map((c) => <option key={c.tenantId} value={c.tenantId}>{c.name}</option>)}
          </select>
        )}
      </div>

      {team !== null && (
        <div className="bu-tabs" role="tablist">
          <button role="tab" className={tab === 'people' ? 'on' : ''} onClick={(): void => setTab('people')}><Icon name="users" size={16} /> People <span className="bu-count">{counts.active + counts.invited}</span></button>
          <button role="tab" className={tab === 'roles' ? 'on' : ''} onClick={(): void => setTab('roles')}><Icon name="key" size={16} /> Roles <span className="bu-count">{team.roles.length}</span></button>
          <span style={{ flex: 1 }} />
          <span style={{ alignSelf: 'center' }}><ProviderTag p={team.client.provider} domain={team.client.domain} /></span>
        </div>
      )}

      {msg !== null && <div className={`bu-alert ${msg.ok ? 'bu-alert-ok' : 'bu-alert-danger'}`} style={{ marginBottom: 16 }}>{msg.text}</div>}
      {team === null ? (msg === null && <div className="bu-card bu-card-pad" style={{ color: 'var(--text-3)' }}>Loading...</div>) : tab === 'people' ? renderPeople(team) : renderRoles(team)}
      {team !== null && renderInvite(team)}
      {team !== null && renderRoleDrawer(team)}
    </AdminLayout>
  );
};
